import { Box, BoxProps } from '@chakra-ui/react';
import { Checkbox } from '@main/complykit/components/checkbox';
import { useStableCallback } from '@main/shared/browser';
import {
  Cell,
  Column,
  ColumnDef,
  ColumnOrderState,
  Row,
  RowSelectionState,
  Table,
} from '@tanstack/react-table';
import {
  cloneElement,
  createContext,
  Dispatch,
  FC,
  forwardRef,
  MouseEventHandler,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
  BulkAction,
  BulkActionBar,
  ChakraMenuActionItem,
  WrappedBulkActionState,
} from '../bulk-action-bar';
import { CellContainer } from './shared/cell';
import { actionsColumnId, selectionColumnId } from './table-column-ids';

/** Common bulk change parameters. Chakra-engine config: onClick gets a real MouseEvent. */
export type BulkChangeConfigBase = ChakraMenuActionItem;
export type BulkChangeConfigCallback<in out T, R> = (rows: Row<T>[]) => R | undefined;

/** Bulk Actions */

export type BulkActionConfigItem = BulkChangeConfigBase & {
  onClick: MouseEventHandler<HTMLButtonElement>;
};
export type BulkActionConfigCallback<T> = BulkChangeConfigCallback<T, BulkActionConfigItem>;
export type BulkActionConfig<T> = BulkActionConfigItem | BulkActionConfigCallback<T>;
export type BulkActionsConfig<T> = BulkActionConfig<T> | BulkActionConfig<T>[];

/** Bulk Edits */

export type BulkEditableFieldConfigItem<T> = Omit<
  Omit<BulkChangeConfigBase, 'wrapper'>,
  'onChange'
> & {
  id: string;
  editor: (
    rows: Row<T>[],
    trigger: JSX.Element,
    isInMenu: boolean,
    onOpenChange: (isOpen: boolean) => void,
    labelledBy: string,
  ) => JSX.Element;
};
type BulkEditableFieldConfigCallback<T> = BulkChangeConfigCallback<
  T,
  BulkEditableFieldConfigItem<T>
>;
export type BulkEditableFieldConfig<T> =
  | BulkEditableFieldConfigItem<T>
  | BulkEditableFieldConfigCallback<T>;

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, @typescript-eslint/no-explicit-any
export const hasBulkChanges = <T extends unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[],
  rows: T[],
) => {
  return columns.some((column) => {
    const actions = column.meta?.bulkActions;
    const editable = column.meta?.editable;
    const bulkEditableField = column.meta?.bulkEdit;
    const someRowIsEditable = editable && bulkEditableField && rows.some((row) => editable(row));
    return (actions && (Array.isArray(actions) ? actions.length > 0 : true)) || someRowIsEditable;
  });
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint, @typescript-eslint/no-explicit-any
export const useHasBulkChanges = <T extends unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[],
  rows: T[],
) => useMemo(() => hasBulkChanges(columns, rows), [columns, rows]);

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const useBulkChangeConfigs = <T extends unknown>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: Column<T, any>[],
  columnOrder: ColumnOrderState,
  selectedRows: Row<T>[],
) => {
  return useMemo(
    () =>
      [...columnOrder, actionsColumnId].reduce<{
        fields: BulkEditableFieldConfigItem<T>[];
        actions: BulkActionConfigItem[];
      }>(
        (acc, columnId) => {
          const column = columns.find(({ id }) => id === columnId);
          const actions = column?.columnDef.meta?.bulkActions;
          const editableField = column?.columnDef.meta?.bulkEdit;

          if (actions) {
            const configArray = Array.isArray(actions) ? actions : [actions];
            return {
              ...acc,
              actions: [
                ...acc.actions,
                ...configArray.reduce<BulkActionConfigItem[]>((acc, item) => {
                  const finalConfigItem = typeof item === 'function' ? item(selectedRows) : item;
                  if (finalConfigItem && 'onClick' in finalConfigItem) {
                    return [...acc, finalConfigItem];
                  }
                  return acc;
                }, []),
              ],
            };
          }

          if (editableField) {
            const finalConfigItem =
              typeof editableField === 'function' ? editableField(selectedRows) : editableField;
            if (finalConfigItem && 'editor' in finalConfigItem) {
              return {
                ...acc,
                fields: [...acc.fields, finalConfigItem],
              };
            }
          }
          return acc;
        },
        {
          fields: [],
          actions: [],
        },
      ),
    [columnOrder, columns, selectedRows],
  );
};

export const useRowSelectionState = () => {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  return useMemo(() => {
    const selectedRows = Object.entries(rowSelection).reduce<string[]>(
      (selectedRowsAccumulator, [id, isSelected]) =>
        isSelected ? [...selectedRowsAccumulator, id] : selectedRowsAccumulator,
      [],
    );
    return [rowSelection, setRowSelection, selectedRows] as const;
  }, [rowSelection]);
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useDeselectRemovedRows = <T extends unknown>({
  table,
  rowSelection,
  setRowSelection,
}: {
  table: Table<T>;
  rowSelection: RowSelectionState;
  setRowSelection: Dispatch<SetStateAction<RowSelectionState>>;
}) => {
  const rowsById = table.getRowModel().rowsById;
  useEffect(() => {
    const nonexistentRows = Object.keys(rowSelection).filter((id) => !rowsById[id]);
    if (nonexistentRows.length > 0) {
      setRowSelection((prevRowSelection) =>
        Object.entries(prevRowSelection).reduce(
          (newState, [id, value]) =>
            id in rowsById
              ? {
                  ...newState,
                  [id]: value,
                }
              : newState,
          {},
        ),
      );
    }
  }, [rowSelection, rowsById, setRowSelection]);
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const SelectionColumnHeader = <T extends unknown>({ table }: { table: Table<T> }) => {
  const { t } = useTranslation('ui');
  const isAllRowsSelected = table.getIsAllRowsSelected();
  const isIndeterminate = table.getIsSomeRowsSelected();
  return (
    <Box display="flex" alignItems="center" py={3} my={-3} px={2} mx={-2}>
      <Checkbox
        size="md"
        isSelected={isAllRowsSelected}
        isIndeterminate={isIndeterminate}
        onChange={() => table.toggleAllPageRowsSelected(!table.getIsAllPageRowsSelected())}
        aria-label={
          isAllRowsSelected
            ? t('table.bulkSelection.deselectAll')
            : t('table.bulkSelection.selectAll')
        }
      />
    </Box>
  );
};

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const SelectionColumnCell = <T extends unknown>({
  row,
  cell,
}: {
  row: Row<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  cell: Cell<T, any>;
}) => {
  const { t } = useTranslation('ui');
  const isSelected = row.getIsSelected();
  return (
    <CellContainer
      cell={cell}
      p={0}
      alignItems="center"
      onClick={(e) => {
        e.stopPropagation();
      }}
    >
      <Box display="flex" alignItems="center" height="100%" px={2}>
        <Checkbox
          size="md"
          isSelected={isSelected}
          isDisabled={!row.getCanSelect()}
          onChange={(nextSelected) => row.toggleSelected(nextSelected)}
          aria-label={
            isSelected ? t('table.bulkSelection.deselectRow') : t('table.bulkSelection.selectRow')
          }
        />
      </Box>
    </CellContainer>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const selectionColumn: ColumnDef<any> = {
  id: selectionColumnId,
  enableResizing: false,
  size: 32,
  header: ({ table }) => <SelectionColumnHeader table={table} />,
  cell: ({ row, cell }) => <SelectionColumnCell row={row} cell={cell} />,
};

export const BulkSelectionToolbar: FC<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  table: Table<any>;
  tableName: string;
  selectedItems: string[];
  portalRef?: RefObject<HTMLElement>;
}> = ({ table, tableName, selectedItems, portalRef }) => {
  const selectedRows = table.getSelectedRowModel().rows;
  const bulkEditTriggerIdPrefix = useId().replace(/:/g, '');
  const bulkChangeConfigs = useBulkChangeConfigs(
    table.getAllColumns(),
    table.getState().columnOrder,
    selectedRows,
  );
  const isLoading = useIsBulkLoading();

  const primaryActions: BulkAction<WrappedBulkActionState>[] = useMemo(
    () =>
      bulkChangeConfigs.fields.map((field) => {
        const inlineTriggerId = `bulk-edit-${bulkEditTriggerIdPrefix}-${field.id}-button`;
        const overflowTriggerId = `bulk-edit-${bulkEditTriggerIdPrefix}-${field.id}-menuitem`;

        return {
          id: field.id,
          label: field.label,
          icon: field.icon,
          tooltip: field.tooltip ?? '',
          disabledTooltip: field.disabledTooltip ?? '',
          isDisabled: field.isDisabled ?? false,
          isLoading: isLoading[field.id] || field.isLoading,
          buttonProps: { id: inlineTriggerId },
          wrapper: (menuItem, { isVisible, onBulkEditorOpenChange }) =>
            field.editor(
              selectedRows,
              isVisible ? menuItem : cloneElement(menuItem, { id: overflowTriggerId }),
              !isVisible,
              (isOpen) => onBulkEditorOpenChange(field.id, isOpen),
              isVisible ? inlineTriggerId : overflowTriggerId,
            ),
        };
      }),
    [bulkChangeConfigs.fields, bulkEditTriggerIdPrefix, isLoading, selectedRows],
  );

  const toolbar = (
    <BulkActionBar
      name={tableName}
      selectionSize={selectedItems.length}
      primaryActions={primaryActions}
      secondaryActions={bulkChangeConfigs.actions}
      onDeselectAll={() => table.resetRowSelection(true)}
      cardProps={{ bottom: 6, zIndex: 2 }}
      primaryMenuActionProps={{ closeOnSelect: false }}
    />
  );

  return portalRef
    ? portalRef.current
      ? createPortal(toolbar, portalRef.current)
      : null
    : toolbar;
};

export const BulkSelectionToolbarPortal = forwardRef<HTMLElement, BoxProps>((props, ref) => (
  <Box {...props} display="contents" ref={ref} />
));

type BulkSelectionLoadingContextValue = {
  setIsLoading: (id: string, isLoading: boolean) => void;
  isLoading: Record<string, boolean>;
};
export const bulkSelectionLoadingContext = createContext<BulkSelectionLoadingContextValue>({
  setIsLoading: () => {
    /** */
  },
  isLoading: {},
});

export const BulkSelectionLoadingProvider: FC<PropsWithChildren> = ({ children }) => {
  const [isLoading, setIsLoadingState] = useState<Record<string, boolean>>({});
  const setIsLoading = useStableCallback((id: string, isLoading: boolean) =>
    setIsLoadingState((prevState) => ({ ...prevState, [id]: isLoading })),
  );
  const contextValue = useMemo(() => ({ setIsLoading, isLoading }), [setIsLoading, isLoading]);
  return (
    <bulkSelectionLoadingContext.Provider value={contextValue}>
      {children}
    </bulkSelectionLoadingContext.Provider>
  );
};

const useBulkSelectionLoadingContext = () => useContext(bulkSelectionLoadingContext);

const useSetBulkLoading = (id: string) => {
  const { setIsLoading } = useBulkSelectionLoadingContext();
  return useStableCallback((isLoading: boolean) => setIsLoading(id, isLoading));
};

export const useSyncIsBulkLoading = (id: string, isLoading: boolean) => {
  const setIsLoading = useSetBulkLoading(id);
  useEffect(() => {
    setIsLoading(isLoading);
  }, [setIsLoading, isLoading]);
};

const useIsBulkLoading = () => {
  const { isLoading } = useBulkSelectionLoadingContext();
  return isLoading;
};
